import {
  buildCarePlanGeniePreview,
  CarePlanGenieFieldDefinition,
  CarePlanGenieQuestion,
} from "./carePlanGenieShared";

export interface TreatmentProgramGenieSectionValues {
  [key: string]: any;
}

type TreatmentOption = {
  id: string;
  label: string;
  description: string;
  detailText?: string;
  assistanceText?: string;
  extraUpdates?: Record<string, any>;
};

type TreatmentSectionConfig = {
  id: string;
  title: string;
  toggleField: string;
  detailField: string;
  assistanceField: string;
  statusPrompt: string;
  statusOptions: TreatmentOption[];
  interventionPrompt: string;
  interventionOptions: TreatmentOption[];
};

const createTreatmentQuestion = (config: TreatmentSectionConfig): CarePlanGenieQuestion => ({
  id: config.id,
  title: config.title,
  prompt: config.statusPrompt,
  helperText: config.interventionPrompt,
  editableFieldKeys: [config.detailField, config.assistanceField],
  options: [
    ...config.statusOptions.map((option) => ({
      id: option.id,
      label: option.label,
      description: option.description,
      groupLabel: "Treatment details",
      updates: {
        [config.toggleField]: true,
        ...(option.detailText ? { [config.detailField]: option.detailText } : {}),
        ...(option.extraUpdates || {}),
      },
    })),
    {
      id: `${config.id}_status_none`,
      label: `No ${config.title.toLowerCase()} needs`,
      description: `No current ${config.title.toLowerCase()} concerns need to be documented.`,
      groupLabel: "Treatment details",
      updates: {
        [config.toggleField]: false,
      },
    },
    ...config.interventionOptions.map((option) => ({
      id: option.id,
      label: option.label,
      description: option.description,
      groupLabel: "Caregiver interventions",
      updates: {
        [config.toggleField]: true,
        ...(option.assistanceText ? { [config.assistanceField]: option.assistanceText } : {}),
        ...(option.extraUpdates || {}),
      },
    })),
  ],
});

const TREATMENT_SECTIONS: TreatmentSectionConfig[] = [
  {
    id: "oxygen",
    title: "Oxygen",
    toggleField: "anyOxygenUse",
    detailField: "oxygenUseDetails",
    assistanceField: "oxygenUseAssistanceDetails",
    statusPrompt: "What best describes the resident's oxygen use?",
    statusOptions: [
      {
        id: "oxygen_continuous",
        label: "Continuous oxygen use",
        description: "Oxygen is used routinely through the day or night.",
        detailText: "Resident uses oxygen on a routine basis and needs caregiver awareness of ordered flow, equipment setup, and respiratory status.",
      },
      {
        id: "oxygen_as_needed",
        label: "Oxygen used as needed",
        description: "Oxygen may be used during symptoms, activity, or recovery periods.",
        detailText: "Resident may use oxygen as needed during shortness of breath, activity, or recovery periods based on the care plan or provider instruction.",
      },
      {
        id: "oxygen_portable",
        label: "Portable oxygen or tank safety needs",
        description: "Portable equipment handling should be included.",
        detailText: "Resident uses portable oxygen equipment and needs monitoring for tubing, tank safety, and travel readiness when away from the room or home.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for oxygen support?",
    interventionOptions: [
      {
        id: "oxygen_intervention_safety",
        label: "Check equipment and safety",
        description: "Caregivers verify tubing, cannula placement, and general safety.",
        assistanceText: "Caregivers should check tubing placement, cannula fit, oxygen safety, and report equipment issues or signs of respiratory decline.",
      },
      {
        id: "oxygen_intervention_monitoring",
        label: "Monitor breathing and tolerance",
        description: "Watch for shortness of breath, fatigue, or distress.",
        assistanceText: "Caregivers should observe breathing, tolerance, and comfort and notify the care team about increasing shortness of breath or distress.",
      },
      {
        id: "oxygen_intervention_reminders",
        label: "Provide reminders and cueing",
        description: "Resident may need cueing to keep oxygen in place or use it correctly.",
        assistanceText: "Caregivers should provide reminders or cueing for correct oxygen use and encourage compliance with the ordered routine.",
      },
    ],
  },
  {
    id: "dialysis",
    title: "Dialysis",
    toggleField: "anyDialysis",
    detailField: "dialysisDetails",
    assistanceField: "dialysisAssistanceDetails",
    statusPrompt: "What best describes the resident's dialysis-related needs?",
    statusOptions: [
      {
        id: "dialysis_regular",
        label: "Regular dialysis schedule",
        description: "Resident attends recurring dialysis sessions.",
        detailText: "Resident attends a regular dialysis schedule and may need support related to appointments, fatigue, and recovery after treatment.",
      },
      {
        id: "dialysis_post_treatment",
        label: "Post-dialysis weakness or fatigue",
        description: "Recovery needs after treatment should be noted.",
        detailText: "Resident may have weakness, fatigue, or variable tolerance after dialysis treatments and should be monitored for recovery needs.",
      },
      {
        id: "dialysis_fluid_monitoring",
        label: "Fluid or symptom monitoring",
        description: "Changes in weight, swelling, or symptoms are important.",
        detailText: "Resident may need monitoring for swelling, fluid status, blood pressure changes, or other symptoms between dialysis treatments.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for dialysis support?",
    interventionOptions: [
      {
        id: "dialysis_intervention_coordination",
        label: "Coordinate appointments and transport",
        description: "Caregivers help keep the dialysis schedule consistent.",
        assistanceText: "Caregivers should help coordinate dialysis appointments, transportation, and follow-up communication related to treatment.",
      },
      {
        id: "dialysis_intervention_monitor",
        label: "Monitor recovery after treatment",
        description: "Observe fatigue, appetite, dizziness, or changes in condition.",
        assistanceText: "Caregivers should monitor post-dialysis fatigue, dizziness, appetite, and overall recovery and report concerning changes promptly.",
      },
      {
        id: "dialysis_intervention_report",
        label: "Report symptom changes",
        description: "Escalate swelling, shortness of breath, or other concerns.",
        assistanceText: "Caregivers should report swelling, breathing changes, dizziness, or other concerning symptoms related to dialysis needs.",
      },
    ],
  },
  {
    id: "blood_thinner",
    title: "Blood thinner",
    toggleField: "anyBloodThinners",
    detailField: "bloodThinnersDetails",
    assistanceField: "bloodThinnersAssistanceDetails",
    statusPrompt: "What best describes the resident's blood thinner or anticoagulant needs?",
    statusOptions: [
      {
        id: "blood_thinner_med",
        label: "Uses blood thinner medication",
        description: "Medication safety and bleeding risk should be included.",
        detailText: "Resident uses blood thinning medication and should be monitored for bleeding risk, bruising, or medication-related concerns.",
        extraUpdates: {
          anyAntiCoagulantTherapy: true,
        },
      },
      {
        id: "blood_thinner_inr",
        label: "Needs INR or lab monitoring",
        description: "Lab follow-up is part of the treatment plan.",
        detailText: "Resident needs INR or lab monitoring related to anticoagulant therapy and follow-up should remain current in the care plan.",
        extraUpdates: {
          anyINR_LABMonitoring: true,
          anyAntiCoagulantTherapy: true,
        },
      },
      {
        id: "blood_thinner_bleeding_history",
        label: "Bleeding or bruising concerns",
        description: "Extra caution is needed around injury or bleeding symptoms.",
        detailText: "Resident may be at higher risk for bruising or bleeding and caregiver should be alert to symptom changes or injury-related concerns.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for blood thinner support?",
    interventionOptions: [
      {
        id: "blood_thinner_intervention_monitor",
        label: "Watch for bleeding or bruising",
        description: "Observe skin, gums, stools, urine, or injury sites.",
        assistanceText: "Caregivers should watch for bruising, bleeding, falls, or other changes that may suggest anticoagulant complications and report them promptly.",
      },
      {
        id: "blood_thinner_intervention_labs",
        label: "Support lab follow-up",
        description: "Help with reminders or coordination for INR and lab checks.",
        assistanceText: "Caregivers should support reminders and coordination for ordered INR or lab follow-up related to blood thinner therapy.",
      },
      {
        id: "blood_thinner_intervention_safety",
        label: "Use extra safety precautions",
        description: "Reduce injury risk during daily care and mobility.",
        assistanceText: "Caregivers should use extra safety precautions during transfers, mobility, and personal care to reduce injury risk while on blood thinners.",
      },
    ],
  },
  {
    id: "blood_glucose",
    title: "Blood Glucose",
    toggleField: "anyBloodGlucoseMonitoring",
    detailField: "bloodGlucoseMonitoringDetails",
    assistanceField: "bloodGlucoseMonitoringAssistanceDetails",
    statusPrompt: "What best describes the resident's blood glucose-related needs?",
    statusOptions: [
      {
        id: "blood_glucose_checks",
        label: "Routine blood glucose checks",
        description: "Resident needs recurring glucose monitoring.",
        detailText: "Resident requires routine blood glucose monitoring as part of diabetes or metabolic care.",
      },
      {
        id: "blood_glucose_variable",
        label: "Variable readings or symptom risk",
        description: "Highs, lows, or symptoms should be tracked closely.",
        detailText: "Resident may have variable blood glucose readings or symptoms of high or low blood sugar that require ongoing observation.",
      },
      {
        id: "blood_glucose_diet_relation",
        label: "Food intake affects glucose control",
        description: "Meals, snacks, or poor intake are part of the assessment.",
        detailText: "Resident's glucose management may be affected by meal intake, appetite changes, or inconsistent food consumption.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for blood glucose support?",
    interventionOptions: [
      {
        id: "blood_glucose_intervention_checks",
        label: "Perform and document checks",
        description: "Caregivers complete glucose checks as directed.",
        assistanceText: "Caregivers should complete blood glucose checks as directed, document results, and communicate readings outside the expected range.",
      },
      {
        id: "blood_glucose_intervention_symptoms",
        label: "Watch for symptoms of highs and lows",
        description: "Observe shakiness, confusion, sweating, or lethargy.",
        assistanceText: "Caregivers should observe for symptoms of low or high blood sugar and follow the care plan promptly when symptoms occur.",
      },
      {
        id: "blood_glucose_intervention_meals",
        label: "Support meal timing and intake",
        description: "Consistent meals and snacks may be important to treatment success.",
        assistanceText: "Caregivers should support meal timing, encourage appropriate intake, and report poor appetite or intake that may affect glucose control.",
      },
    ],
  },
  {
    id: "injections",
    title: "Injections",
    toggleField: "anyInjectionUse",
    detailField: "injectionUseDetails",
    assistanceField: "injectionUseAssistanceDetails",
    statusPrompt: "What best describes the resident's injection-related needs?",
    statusOptions: [
      {
        id: "injections_regular",
        label: "Regular injection treatment",
        description: "Resident receives scheduled injections.",
        detailText: "Resident receives scheduled injection treatment and caregiver should be aware of timing, purpose, and tolerance.",
      },
      {
        id: "injections_as_needed",
        label: "Occasional or condition-based injections",
        description: "Injection support varies based on symptoms or plan.",
        detailText: "Resident may receive injections based on condition-specific needs or provider instructions and should be monitored for response.",
      },
      {
        id: "injections_site_monitoring",
        label: "Injection site or tolerance monitoring",
        description: "Site reactions or medication tolerance should be assessed.",
        detailText: "Resident may need observation for injection site concerns, discomfort, or medication tolerance after injections.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for injection support?",
    interventionOptions: [
      {
        id: "injections_intervention_schedule",
        label: "Keep schedule and coordination current",
        description: "Support consistent timing and communication.",
        assistanceText: "Caregivers should help keep injection timing, follow-up, and communication current according to the care plan.",
      },
      {
        id: "injections_intervention_monitor",
        label: "Monitor response and injection site",
        description: "Observe for redness, swelling, pain, or changes after administration.",
        assistanceText: "Caregivers should observe response after injections and report redness, swelling, pain, or other concerns at the injection site.",
      },
      {
        id: "injections_intervention_comfort",
        label: "Provide reassurance and comfort support",
        description: "Resident may benefit from emotional support around injections.",
        assistanceText: "Caregivers should provide reassurance, comfort, and calm explanation before and after injections when the resident is anxious or uncomfortable.",
      },
    ],
  },
  {
    id: "cpap_bipap",
    title: "CPAP/BIPAP",
    toggleField: "anyCPAP_BiPAP",
    detailField: "CPAP_BiPAPDetails",
    assistanceField: "CPAP_BiPAPAssistanceDetails",
    statusPrompt: "What best describes the resident's CPAP or BiPAP needs?",
    statusOptions: [
      {
        id: "cpap_bipap_night",
        label: "Uses CPAP/BiPAP routinely",
        description: "Equipment is part of the resident's regular respiratory routine.",
        detailText: "Resident uses CPAP/BiPAP support as part of the regular respiratory routine and may need setup and tolerance monitoring.",
      },
      {
        id: "cpap_bipap_tolerance",
        label: "Has tolerance or compliance issues",
        description: "Mask comfort, anxiety, or refusal may affect use.",
        detailText: "Resident may have difficulty tolerating CPAP/BiPAP due to mask discomfort, anxiety, or inconsistent use.",
      },
      {
        id: "cpap_bipap_cleaning",
        label: "Equipment cleaning or setup is important",
        description: "Routine equipment care is part of the assessment.",
        detailText: "Resident's CPAP/BiPAP routine includes equipment setup, mask placement, and regular cleaning needs.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for CPAP/BiPAP support?",
    interventionOptions: [
      {
        id: "cpap_bipap_intervention_setup",
        label: "Help with setup and mask placement",
        description: "Caregivers make sure the device is ready and positioned correctly.",
        assistanceText: "Caregivers should help with equipment setup, mask placement, and ensuring the device is ready for use as ordered.",
      },
      {
        id: "cpap_bipap_intervention_tolerance",
        label: "Encourage tolerance and routine use",
        description: "Resident may need cueing or reassurance to keep it on.",
        assistanceText: "Caregivers should encourage routine use, provide reassurance, and report ongoing refusal or poor tolerance of CPAP/BiPAP.",
      },
      {
        id: "cpap_bipap_intervention_cleaning",
        label: "Support cleaning and equipment care",
        description: "Routine maintenance helps safety and comfort.",
        assistanceText: "Caregivers should support cleaning, equipment care, and replacement follow-up according to the care plan or device instructions.",
      },
    ],
  },
  {
    id: "nebulizer",
    title: "Nebulizer",
    toggleField: "anyNebulizerUse",
    detailField: "nebulizerUseDetails",
    assistanceField: "nebulizerUseAssistanceDetails",
    statusPrompt: "What best describes the resident's nebulizer-related needs?",
    statusOptions: [
      {
        id: "nebulizer_regular",
        label: "Uses nebulizer treatments",
        description: "Nebulizer is part of the respiratory treatment plan.",
        detailText: "Resident uses nebulizer treatment as part of respiratory support and may need help with setup and follow-through.",
      },
      {
        id: "nebulizer_symptom_based",
        label: "Needs nebulizer during flare-ups",
        description: "Symptoms may increase the need for treatment support.",
        detailText: "Resident may need nebulizer treatment during respiratory flare-ups or periods of increased symptoms.",
      },
      {
        id: "nebulizer_tolerance",
        label: "Needs tolerance or coordination support",
        description: "Resident may need cueing to complete the treatment.",
        detailText: "Resident may need cueing, reassurance, or pacing support to complete nebulizer treatments effectively.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for nebulizer support?",
    interventionOptions: [
      {
        id: "nebulizer_intervention_setup",
        label: "Help with setup and cleaning",
        description: "Caregivers prepare the treatment and keep equipment clean.",
        assistanceText: "Caregivers should help with nebulizer setup, medication preparation if directed, and cleaning after use.",
      },
      {
        id: "nebulizer_intervention_monitor",
        label: "Monitor breathing response",
        description: "Observe whether symptoms improve or worsen.",
        assistanceText: "Caregivers should monitor breathing before and after treatment and report poor response or worsening symptoms.",
      },
      {
        id: "nebulizer_intervention_cueing",
        label: "Cue the resident through treatment",
        description: "Resident may need reminders to stay with the treatment.",
        assistanceText: "Caregivers should provide cueing, reassurance, and pacing support so the resident can complete nebulizer treatment as planned.",
      },
    ],
  },
  {
    id: "range_of_motion",
    title: "Range of motion",
    toggleField: "anyRangeOfMotionExercises",
    detailField: "rangeOfMotionExercisesDetails",
    assistanceField: "rangeOfMotionExercisesAssistanceDetails",
    statusPrompt: "What best describes the resident's range of motion or mobility exercise needs?",
    statusOptions: [
      {
        id: "range_of_motion_regular",
        label: "Routine exercises are part of care",
        description: "Regular ROM or mobility work is needed.",
        detailText: "Resident benefits from range of motion exercises or mobility support to maintain comfort and function.",
      },
      {
        id: "range_of_motion_stiffness",
        label: "Stiffness, weakness, or contracture risk",
        description: "Preventive support is important.",
        detailText: "Resident may have stiffness, weakness, pain, or contracture risk that makes range of motion support important.",
      },
      {
        id: "range_of_motion_therapy_plan",
        label: "Exercises follow therapy recommendations",
        description: "ROM plan may come from PT/OT or provider guidance.",
        detailText: "Resident's range of motion routine may follow therapy or provider recommendations and should be completed consistently when appropriate.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for range of motion support?",
    interventionOptions: [
      {
        id: "range_of_motion_intervention_assist",
        label: "Assist or cue exercises",
        description: "Caregivers help the resident complete exercises safely.",
        assistanceText: "Caregivers should assist or cue exercises as instructed and monitor tolerance during activity.",
      },
      {
        id: "range_of_motion_intervention_positioning",
        label: "Use positioning and comfort support",
        description: "Positioning may help comfort and participation.",
        assistanceText: "Caregivers should use positioning, pacing, and comfort support to help the resident participate safely in range of motion activity.",
      },
      {
        id: "range_of_motion_intervention_report",
        label: "Report pain or reduced tolerance",
        description: "Changes in movement tolerance should be communicated.",
        assistanceText: "Caregivers should report pain, swelling, refusal, or reduced movement tolerance that affects the exercise plan.",
      },
    ],
  },
  {
    id: "pt_ot_st",
    title: "PT/OT/ST",
    toggleField: "anyPT_OT_ST_Therapies",
    detailField: "PT_OT_ST_TherapiesDetails",
    assistanceField: "PT_OT_ST_TherapiesAssistanceDetails",
    statusPrompt: "What best describes the resident's PT, OT, or speech therapy needs?",
    statusOptions: [
      {
        id: "pt_ot_st_active",
        label: "Actively receiving therapy",
        description: "PT, OT, or ST is currently part of the care plan.",
        detailText: "Resident participates in PT, OT, or ST services and may need care coordination around goals and recommendations.",
      },
      {
        id: "pt_ot_st_followup",
        label: "Needs carryover from therapy plan",
        description: "Daily care should reinforce therapy recommendations.",
        detailText: "Resident may need daily carryover of therapy recommendations related to mobility, function, swallowing, communication, or safety.",
      },
      {
        id: "pt_ot_st_schedule",
        label: "Appointments and therapy follow-through matter",
        description: "Routine coordination is part of the assessment.",
        detailText: "Resident's therapy participation may depend on scheduling, follow-through, and caregiver support between visits.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for PT/OT/ST support?",
    interventionOptions: [
      {
        id: "pt_ot_st_intervention_carryover",
        label: "Carry over therapy recommendations",
        description: "Caregivers reinforce exercises or safety strategies.",
        assistanceText: "Caregivers should help carry over therapy recommendations and support follow-through with exercises or strategies between visits.",
      },
      {
        id: "pt_ot_st_intervention_coordinate",
        label: "Coordinate visits and communication",
        description: "Keep therapists and caregiver aligned on the resident's progress.",
        assistanceText: "Caregivers should coordinate scheduling and communicate notable changes that may affect therapy goals or tolerance.",
      },
      {
        id: "pt_ot_st_intervention_monitor",
        label: "Monitor tolerance and progress",
        description: "Watch for fatigue, pain, or changes in function.",
        assistanceText: "Caregivers should monitor fatigue, pain, participation, and progress and report concerns that affect therapy follow-through.",
      },
    ],
  },
  {
    id: "nurse_delegation",
    title: "Nurse Delegation",
    toggleField: "anyNurseDelegatedProcedures",
    detailField: "nurseDelegatedProceduresDetails",
    assistanceField: "nurseDelegatedProceduresAssistanceDetails",
    statusPrompt: "What best describes the resident's nurse delegation needs?",
    statusOptions: [
      {
        id: "nurse_delegation_active",
        label: "Active delegated procedures",
        description: "Delegated tasks are part of routine daily care.",
        detailText: "Resident receives care involving nurse delegated procedures that must follow current instructions and oversight.",
      },
      {
        id: "nurse_delegation_updates",
        label: "Delegation instructions require follow-up",
        description: "Renewals, updates, or competency support matter.",
        detailText: "Resident's delegated care may require ongoing instruction review, renewal, or communication with the delegating nurse.",
      },
      {
        id: "nurse_delegation_monitoring",
        label: "Condition changes affect delegated care",
        description: "Changes in status may require prompt reporting.",
        detailText: "Resident's condition changes may affect how delegated tasks should be completed and may require prompt nurse notification.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for nurse delegation support?",
    interventionOptions: [
      {
        id: "nurse_delegation_intervention_follow",
        label: "Follow delegation instructions closely",
        description: "Caregivers follow the current approved process exactly.",
        assistanceText: "Caregivers should follow delegation instructions closely and complete tasks only as approved by the delegating nurse.",
      },
      {
        id: "nurse_delegation_intervention_report",
        label: "Report condition changes promptly",
        description: "Escalate new symptoms or concerns that affect delegated tasks.",
        assistanceText: "Caregivers should report condition changes, task difficulty, or concerns promptly to the nurse and care team.",
      },
      {
        id: "nurse_delegation_intervention_document",
        label: "Document task completion and concerns",
        description: "Documentation supports safety and continuity.",
        assistanceText: "Caregivers should document delegated task completion and note concerns or unusual findings according to the care plan.",
      },
    ],
  },
  {
    id: "other_health_issues",
    title: "Other health issues",
    toggleField: "anyOtherTreatmentPrograms",
    detailField: "otherTreatmentProgramsDetails",
    assistanceField: "otherTreatmentProgramsAssistanceDetails",
    statusPrompt: "What best describes other treatment-related health issues or programs?",
    statusOptions: [
      {
        id: "other_health_issues_present",
        label: "Other treatment-related issues are present",
        description: "Additional supports should be documented here.",
        detailText: "Resident has other treatment-related health issues or programs that should be documented in this section for caregiver awareness.",
      },
      {
        id: "other_health_issues_monitoring",
        label: "Needs monitoring for additional health concerns",
        description: "Caregiver observation is important even if the issue is not daily.",
        detailText: "Resident may have additional treatment-related concerns that require monitoring or follow-up when symptoms or routine change.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for other treatment-related issues?",
    interventionOptions: [
      {
        id: "other_health_issues_intervention_follow",
        label: "Follow individualized treatment instructions",
        description: "Caregivers use the specific guidance attached to the health issue.",
        assistanceText: "Caregivers should follow any added treatment support instructions and report changes related to these health issues.",
      },
      {
        id: "other_health_issues_intervention_observe",
        label: "Observe and report changes",
        description: "Condition changes should be communicated quickly.",
        assistanceText: "Caregivers should observe for symptom changes, tolerance concerns, or new issues related to the resident's other treatment needs.",
      },
    ],
  },
  {
    id: "home_health",
    title: "Home Health Therapy",
    toggleField: "anyHomeHealthServices",
    detailField: "homeHealthServicesDetails",
    assistanceField: "homeHealthServicesAssistanceDetails",
    statusPrompt: "What best describes the resident's home health service needs?",
    statusOptions: [
      {
        id: "home_health_active",
        label: "Currently receiving home health services",
        description: "Skilled visits are part of the current plan.",
        detailText: "Resident receives home health services that should be coordinated with the overall care plan.",
      },
      {
        id: "home_health_multiple_disciplines",
        label: "More than one home health discipline involved",
        description: "Multiple clinicians or services may be part of support.",
        detailText: "Resident may receive home health support from more than one discipline and coordination between services is important.",
      },
      {
        id: "home_health_followup",
        label: "Home health recommendations affect daily care",
        description: "Caregiver routines may need to align with skilled guidance.",
        detailText: "Home health recommendations may affect the resident's daily care routine, safety, mobility, or treatment follow-through.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for home health support?",
    interventionOptions: [
      {
        id: "home_health_intervention_coordination",
        label: "Coordinate visits and communication",
        description: "Keep the home and visiting team aligned.",
        assistanceText: "Caregivers should support scheduling, communication, and follow-through with home health recommendations.",
      },
      {
        id: "home_health_intervention_prepare",
        label: "Prepare resident and environment for visits",
        description: "Routine setup can help visits go smoothly.",
        assistanceText: "Caregivers should help prepare the resident and environment for home health visits and share relevant updates with the visiting team.",
      },
      {
        id: "home_health_intervention_monitor",
        label: "Watch for changes between visits",
        description: "Important changes should be shared with the skilled team.",
        assistanceText: "Caregivers should monitor changes between home health visits and report concerns that may affect the service plan.",
      },
    ],
  },
  {
    id: "adult_day_health",
    title: "Adult Day health program",
    toggleField: "anyAdultDayHealthPrograms",
    detailField: "adultDayHealthProgramsDetails",
    assistanceField: "adultDayHealthProgramsAssistanceDetails",
    statusPrompt: "What best describes the resident's adult day health program needs?",
    statusOptions: [
      {
        id: "adult_day_health_active",
        label: "Participates in adult day health",
        description: "Program attendance is part of routine structure and support.",
        detailText: "Resident participates in adult day health programming for routine structure, engagement, or supportive services.",
      },
      {
        id: "adult_day_health_benefits",
        label: "Program helps with socialization or routine",
        description: "The program supports engagement, supervision, or community connection.",
        detailText: "Resident may benefit from adult day health for socialization, structured activity, supervision, or supportive daytime routine.",
      },
      {
        id: "adult_day_health_tolerance",
        label: "Attendance depends on health or energy",
        description: "Condition or fatigue may affect participation.",
        detailText: "Resident's day program participation may vary based on health status, fatigue, appointments, or tolerance.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for adult day health support?",
    interventionOptions: [
      {
        id: "adult_day_health_intervention_prepare",
        label: "Prepare resident for attendance",
        description: "Help with readiness, timing, and belongings.",
        assistanceText: "Caregivers may need to help prepare, transport, or coordinate follow-up related to day program participation.",
      },
      {
        id: "adult_day_health_intervention_followup",
        label: "Review updates after program participation",
        description: "Day program feedback may affect the care plan.",
        assistanceText: "Caregivers should review updates, observations, or concerns from the day program and communicate them to the care team when needed.",
      },
      {
        id: "adult_day_health_intervention_monitor",
        label: "Monitor tolerance and participation",
        description: "Energy level, interest, or comfort may change over time.",
        assistanceText: "Caregivers should monitor the resident's tolerance, willingness, and benefit from the day program and report changes affecting attendance.",
      },
    ],
  },
  {
    id: "hospice",
    title: "Hospice details",
    toggleField: "anyHospiceAgencyServices",
    detailField: "hospiceAgencyServicesDetails",
    assistanceField: "hospiceAgencyServicesAssistanceDetails",
    statusPrompt: "What best describes the resident's hospice-related needs?",
    statusOptions: [
      {
        id: "hospice_active",
        label: "Active hospice services",
        description: "Comfort-focused hospice support is in place.",
        detailText: "Resident receives hospice agency services focused on comfort, symptom management, and coordinated support.",
      },
      {
        id: "hospice_symptom_management",
        label: "Comfort and symptom management are a major focus",
        description: "Pain, breathing, appetite, or decline may need close attention.",
        detailText: "Resident's care plan includes comfort-focused support and close observation for symptoms that affect quality of life.",
      },
      {
        id: "hospice_family_support",
        label: "Family and team coordination is important",
        description: "Communication across hospice, family, and caregivers matters.",
        detailText: "Resident's hospice plan may require close coordination between caregivers, hospice caregiver, and family members.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for hospice support?",
    interventionOptions: [
      {
        id: "hospice_intervention_communication",
        label: "Communicate closely with hospice",
        description: "Hospice guidance should stay central to daily care.",
        assistanceText: "Caregivers should communicate closely with hospice and follow guidance related to comfort and symptom changes.",
      },
      {
        id: "hospice_intervention_comfort",
        label: "Provide comfort-focused care",
        description: "Positioning, reassurance, and comfort measures are key.",
        assistanceText: "Caregivers should provide comfort-focused care, observe symptoms closely, and use approved comfort measures as directed by the hospice plan.",
      },
      {
        id: "hospice_intervention_report",
        label: "Report symptom changes promptly",
        description: "Escalate pain, breathing changes, or decline quickly.",
        assistanceText: "Caregivers should report pain, breathing changes, decline, or new distress promptly to hospice and the care team.",
      },
    ],
  },
  {
    id: "other_programs",
    title: "Other Programs",
    toggleField: "anyOtherProgramServices",
    detailField: "otherProgramServicesDetails",
    assistanceField: "otherProgramServicesAssistanceDetails",
    statusPrompt: "What best describes other outside program or service needs?",
    statusOptions: [
      {
        id: "other_programs_active",
        label: "Other outside services are active",
        description: "Additional programs should be listed for coordination.",
        detailText: "Resident receives additional outside services or programs that should be listed in the care plan for coordination.",
      },
      {
        id: "other_programs_periodic",
        label: "Outside services are periodic or episodic",
        description: "Programs may not be daily but still affect the plan.",
        detailText: "Resident may use outside services periodically and caregiver should remain aware of how those supports affect ongoing care.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for other outside programs?",
    interventionOptions: [
      {
        id: "other_programs_intervention_coordinate",
        label: "Coordinate appointments and follow-up",
        description: "Services outside the home should stay connected to the care plan.",
        assistanceText: "Caregivers should help coordinate appointments, communication, and follow-up with outside programs as needed.",
      },
      {
        id: "other_programs_intervention_share_updates",
        label: "Share updates across teams",
        description: "Important changes should not stay siloed in one program.",
        assistanceText: "Caregivers should share relevant updates from outside services with the resident's care team when they affect daily support.",
      },
    ],
  },
  {
    id: "meaningful_day",
    title: "Meaningful day",
    toggleField: "anyInterventionsForMeaningfullDay",
    detailField: "interventionsForMeaningfullDayDetails",
    assistanceField: "interventionsForMeaningfullDayAssistanceDetails",
    statusPrompt: "What best describes the resident's meaningful day support needs?",
    statusOptions: [
      {
        id: "meaningful_day_structure",
        label: "Benefits from structured daily engagement",
        description: "Routine, activity, and purpose support wellbeing.",
        detailText: "Resident benefits from meaningful day interventions that support participation, structure, and quality of life.",
      },
      {
        id: "meaningful_day_preferences",
        label: "Needs activity choices based on interests",
        description: "Individualized preferences are important to participation.",
        detailText: "Resident may do best when activities are individualized around interests, energy level, and preferred daily rhythm.",
      },
      {
        id: "meaningful_day_behavior_support",
        label: "Engagement helps reduce distress or behavior issues",
        description: "Structure may reduce boredom, anxiety, or agitation.",
        detailText: "Meaningful engagement may help reduce boredom, anxiety, agitation, or withdrawal during the day.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for meaningful day support?",
    interventionOptions: [
      {
        id: "meaningful_day_intervention_offer_choices",
        label: "Offer simple activity choices",
        description: "Small options help the resident participate.",
        assistanceText: "Caregivers should offer simple activity choices, encourage participation, and adapt engagement to the resident's interests and tolerance.",
      },
      {
        id: "meaningful_day_intervention_schedule",
        label: "Keep a predictable daily structure",
        description: "Routine may help reduce confusion or distress.",
        assistanceText: "Caregivers should support a predictable daily structure and use familiar activity routines that fit the resident's energy and attention.",
      },
      {
        id: "meaningful_day_intervention_redirect",
        label: "Use activities for redirection",
        description: "Preferred engagement can help during restlessness or distress.",
        assistanceText: "Caregivers should use preferred activities as a redirection tool when the resident is restless, distressed, or disengaged.",
      },
    ],
  },
  {
    id: "physical_enablers",
    title: "Physical Enablers",
    toggleField: "anyPhysicalEnablers",
    detailField: "physicalEnablersDetails",
    assistanceField: "physicalEnablersAssistanceDetails",
    statusPrompt: "What best describes the resident's physical enabler or adaptive device needs?",
    statusOptions: [
      {
        id: "physical_enablers_devices",
        label: "Uses adaptive or supportive devices",
        description: "Devices help positioning, function, comfort, or safety.",
        detailText: "Resident uses physical enablers or supportive devices that help maintain function, positioning, or comfort.",
      },
      {
        id: "physical_enablers_positioning",
        label: "Positioning support is important",
        description: "Body support or alignment may affect comfort and safety.",
        detailText: "Resident may need physical enablers related to positioning, alignment, pressure relief, or body support during routine care.",
      },
      {
        id: "physical_enablers_consistency",
        label: "Correct use and placement matter",
        description: "Improper device use could reduce benefit or create risk.",
        detailText: "Resident benefits most when supportive devices are applied consistently and used as intended during daily care.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for physical enabler support?",
    interventionOptions: [
      {
        id: "physical_enablers_intervention_apply",
        label: "Assist with correct use and placement",
        description: "Caregivers help position and use devices properly.",
        assistanceText: "Caregivers should assist with correct use, placement, and monitoring of supportive devices.",
      },
      {
        id: "physical_enablers_intervention_check_skin",
        label: "Observe comfort and skin tolerance",
        description: "Look for discomfort, redness, or reduced tolerance.",
        assistanceText: "Caregivers should observe for discomfort, skin issues, or poor tolerance related to physical enablers and report concerns promptly.",
      },
      {
        id: "physical_enablers_intervention_consistency",
        label: "Use devices consistently as directed",
        description: "Routine follow-through helps the resident benefit from support.",
        assistanceText: "Caregivers should use supportive devices consistently according to the care plan so the resident receives the intended benefit.",
      },
    ],
  },
  {
    id: "refusal_plan",
    title: "Refusal Plan",
    toggleField: "anyTreatmentRefusalPlan",
    detailField: "treatmentRefusalPlanDetails",
    assistanceField: "treatmentRefusalPlanAssistanceDetails",
    statusPrompt: "What best describes the resident's treatment refusal-related needs?",
    statusOptions: [
      {
        id: "refusal_plan_occasional",
        label: "Occasional refusal or hesitation",
        description: "Resident may hesitate or decline treatment at times.",
        detailText: "Resident may decline treatment at times and benefits from a clear, respectful refusal response plan.",
      },
      {
        id: "refusal_plan_pattern",
        label: "Repeated refusals affect care",
        description: "Patterned refusal creates follow-up and safety concerns.",
        detailText: "Resident may have a pattern of treatment refusal that can affect symptom management, safety, or follow-through with the care plan.",
      },
      {
        id: "refusal_plan_communication",
        label: "Needs calm explanation before accepting care",
        description: "Understanding and timing may influence acceptance.",
        detailText: "Resident may be more likely to accept treatment when approached calmly with simple explanation, reassurance, or re-approach timing.",
      },
    ],
    interventionPrompt: "What caregiver interventions are needed for treatment refusal support?",
    interventionOptions: [
      {
        id: "refusal_plan_intervention_reapproach",
        label: "Use calm re-approach and explanation",
        description: "Give space, explain simply, and try again respectfully.",
        assistanceText: "Caregivers should re-approach calmly, explain the purpose of treatment, and use respectful timing when the resident initially refuses care.",
      },
      {
        id: "refusal_plan_intervention_document",
        label: "Document refusals and triggers",
        description: "Patterns can help improve future care planning.",
        assistanceText: "Caregivers should document treatment refusals, possible triggers, and the resident's response to re-approach or explanation.",
      },
      {
        id: "refusal_plan_intervention_notify",
        label: "Notify the care team when refusals continue",
        description: "Ongoing refusal should not stay unaddressed.",
        assistanceText: "Caregivers should notify the care team when refusals continue or when refusal places the resident at higher risk.",
      },
    ],
  },
];

const TREATMENT_GENIE_FIELDS = TREATMENT_SECTIONS.flatMap((section) => ([
  { key: section.toggleField, label: `Any ${section.title}`, type: "boolean", applyMode: "replace" },
  { key: section.detailField, label: `${section.title} details`, type: "text", applyMode: "append" },
  { key: section.assistanceField, label: `${section.title} assistance details`, type: "text", applyMode: "append" },
]));

const EXTRA_TREATMENT_FIELDS = [
  { key: "anyINR_LABMonitoring", label: "Any INR/LAB monitoring", type: "boolean", applyMode: "replace" },
  { key: "anyAntiCoagulantTherapy", label: "Any anticoagulant therapy", type: "boolean", applyMode: "replace" },
];

export const TREATMENT_PROGRAM_GENIE_FIELD_DEFINITIONS: Record<string, CarePlanGenieFieldDefinition> = [
  ...TREATMENT_GENIE_FIELDS,
  ...EXTRA_TREATMENT_FIELDS,
].reduce((accumulator, field) => ({
  ...accumulator,
  [field.key]: field as CarePlanGenieFieldDefinition,
}), {});

export const TREATMENT_PROGRAM_GENIE_QUESTIONS: CarePlanGenieQuestion[] =
  TREATMENT_SECTIONS.map(createTreatmentQuestion);

export const buildTreatmentProgramGeniePreview = (
  currentValues: Record<string, any>,
  selectedOptions: Record<string, string[]>,
  questionDrafts: Record<string, Record<string, string>>,
) => buildCarePlanGeniePreview(
  TREATMENT_PROGRAM_GENIE_FIELD_DEFINITIONS,
  TREATMENT_PROGRAM_GENIE_QUESTIONS,
  currentValues,
  selectedOptions,
  questionDrafts,
);
